import { useState } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { StoryLink } from "@/components/StoryLink";

// Category accents per design_guidelines.json colors.usage:
// World = brand blue, Science = sage, People = amber. Tech keeps its learned
// violet (documented deviation); sports takes coral.
export const CATEGORY_COLORS = {
  world: "#3A86D4",
  science: "#3A9E72",
  people: "#F59E0B",
  tech: "#8B5CF6",
  sports: "#E8553E",
};

export const StoryCard = ({ article, onClick, delay = 1, isBookmarked = false, onBookmark = null }) => {
  const [imageError, setImageError] = useState(false);

  if (!article) return null;

  const bgColors = {
    world: "#EEF5FF",
    science: "#F0FBF5",
    people: "#FFF8EC",
    tech: "#F5F0FF",
    sports: "#FFF0ED",
  };

  // Category mapping wins over the color stored on the article — older
  // articles carry the pre-spec (inverted) colors baked in at ingestion.
  const color = CATEGORY_COLORS[article.category] || article.color || "#3A86D4";
  const bgColor = bgColors[article.category] || "#EEF5FF";

  const handleBookmarkClick = (e) => {
    e.stopPropagation();
    if (onBookmark) {
      onBookmark();
    }
  };

  const hasValidImage = article.image_url && !imageError;

  return (
    <div
      className="story-card fade-in relative group rounded-lg border border-scoop-border hover:border-scoop-blue focus-within:border-scoop-blue bg-white p-3 transition-colors"
      data-testid={`story-card-${article.id}`}
    >
      {/* Bookmark is a SIBLING of the story link, not a child of it —
          interactive content inside an anchor is invalid and leaves screen
          readers unsure which control they are on. Always visible (touch
          devices have no hover), 40px target. */}
      {onBookmark && (
        <button
          type="button"
          onClick={handleBookmarkClick}
          aria-label={isBookmarked ? "Remove from saved stories" : "Save this story"}
          className={`absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full shadow-sm transition-all ${
            isBookmarked
              ? "bg-scoop-blue text-white"
              : "bg-white/95 text-scoop-muted hover:bg-scoop-blue hover:text-white"
          }`}
          data-testid={`bookmark-btn-${article.id}`}
        >
          {isBookmarked ? (
            <BookmarkCheck className="w-4 h-4" />
          ) : (
            <Bookmark className="w-4 h-4" />
          )}
        </button>
      )}

      <StoryLink
        to={`/story/${article.id}`}
        onActivate={onClick}
        className="block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-scoop-blue focus-visible:ring-offset-2"
      >
      {/* Thumbnail */}
      <div
        className="rounded-lg aspect-[3/2] flex items-center justify-center mb-3.5 overflow-hidden"
        style={{ backgroundColor: bgColor }}
      >
        {hasValidImage ? (
          <img
            src={article.image_url}
            alt={article.headline || article.title}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${bgColor} 0%, ${color}15 100%)` }}
          >
            <span className="text-5xl opacity-80" aria-hidden="true">{article.emoji || "📰"}</span>
          </div>
        )}
      </div>

      {/* Category Tag */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span
          className="text-[0.72rem] font-bold tracking-widest uppercase"
          style={{ color }}
        >
          {article.sub_category || article.category}
        </span>
      </div>

      {/* Headline */}
      <h3 className="card-headline font-display font-bold text-[1.05rem] leading-snug tracking-[-0.01em] text-scoop-ink mb-1.5 transition-colors">
        {article.headline || article.title}
      </h3>

      {/* Deck */}
      <p className="text-[0.875rem] text-scoop-muted leading-[1.55] mb-2.5">
        {article.deck}
      </p>

      {/* Footer. These were 0.67rem/0.62rem (~10px) on a product read by
          six-year-olds, and the trust badge — the whole reason a parent picks
          this — was the smallest, lowest-contrast text on the card. At a
          legible 12px the two no longer fit on one line, so the badge gets
          its own row: it earns the space, and nothing wraps mid-phrase. */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[0.75rem] font-semibold tracking-wide uppercase text-scoop-muted whitespace-nowrap">
          {article.read_time} min read
        </span>
        <span className="self-start text-[0.75rem] font-bold tracking-wide uppercase bg-gray-100 text-gray-700 px-2 py-0.5 rounded whitespace-nowrap">
          ✓ Checked for ages 6–9
        </span>
      </div>
      </StoryLink>
    </div>
  );
};

export default StoryCard;
